import React, { useCallback, useEffect, useRef, useState } from "react";
import { Checkbox, DatePicker, Divider, notification, Spin, Typography } from 'antd';
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { cloneDeep, debounce, isEqual, isEmpty } from "lodash";

import StepThree from '../step3';

import Input from "../../../../../../../components/uiComponent/inputs/input/index"
import ReactSelect from '../../../../../../../components/uiComponent/select';
import Button from "../../../../../../../components/uiComponent/button/index";
import Modal from "../../../../../../../components/uiComponent/modal";

import DeleteIcon from '../../../../../../../assets/icons/delete-border-icon.svg';
import AddIcon from '../../../../../../../assets/icons/add-icon-border.svg';
import AlterIcon1 from '../../../../../../../assets/icons/AlertIcon1.svg';

import { ContactInformationWrapper, AlertModalStyleWrapper } from '../../style';
import { sellerFreightClassValues } from "../../../../../../fbaInbound/constants";
import { 
  CreatePallets,
  GetWarehouseShipments,
  ListDeliveryWindowOptions, 
  ListTransportationOptions,
  RevertInboundPlanStatus
} from "../../../../../../../slices/post-shipment";

import { useParams } from "react-router-dom";
import dayjs from 'dayjs';

const stackableOptions = [
  { value: 'Yes', label: 'Yes' },
  { value: 'No', label: 'No' },
];

const initialPallet = {
  length: 40,
  width: 48,
  height: 0,
  weight: 0,
  stackable: null,
  noOfPallets: 0,
};


const ShipmentPallets = ({
  contactDetails,
  setContactDetails,
  shipmentsData,
  setShipmentsData,
  deletedPallets,
  setDeletedPallets,
  setActiveKey,
  activeKey,
  showTransportGlobal,
  setTransportationDetails,
  transportationDetails,
  updateShipmentTrackingDetailsLoading,
  deliveryWindowOptionsLoading,
  palletsChangeAllowed,
  setPalletsChangeAllowed,
  setIsSubmitPackingInfo
}) => {
  const {
    shipmentDetails,
    fetchingWarehouseShipments,
    creatingPallets,
    listingTransportationOptions,
    generatingTransportationOptions,
    generatingDeliveryWindowOptions,
    confirmingTransportationOption,
    shipmentType
  } = useSelector((state) => state.postShipment || {});
  const dispatch = useDispatch();
  const { workOrderId } = useParams();

  const [autoSave, setAutoSave] = useState(false);
  const [editConfirmationModal, setEditConfirmationModal] = useState(false);

  const { shipments } = shipmentDetails || {};

  const { amazonInboundPlanId: inboundPlanId } = shipmentDetails || {};

  useEffect(() => {
    if (activeKey !== '3') return;
    if (isEmpty(shipmentDetails) || listingTransportationOptions) return;
    
    if (shipmentDetails?.inboundStatus === 'TRANSPORTATION_CONFIRMED') return;

    const shipmentIds = shipments.map(({ amazon_shipment_id: shipmentId }) => shipmentId) || [];
    dispatch(ListDeliveryWindowOptions({ inboundPlanId, shipmentIds }));

    dispatch(ListTransportationOptions({
      inboundPlanId,
      placementOptionId: shipments[0]?.amazon_placement_option_id
    })).then(() => {
      dispatch(GetWarehouseShipments({
        workOrderId
      }))
    })
    
  }, []);

  useEffect(() => {
    if (!shipments?.length) return;
    setShipmentsData(shipments.map(({ 
      amazon_reference_id,
      amazon_shipment_id,
      amazon_shipment_confirmation_id, 
      pallets = [], 
      freight_value = '', 
      freight_class = '', 
      boxes_info = [], 
      amazon_warehouse_id, 
      shipment_items_info
     }) => {
      return {
        amazon_reference_id,
        boxes_info,
        amazon_warehouse_id,
        amazon_shipment_id,
        shipment_items_info,
        amazon_shipment_confirmation_id,
        freightInfo: {
          value: freight_value || '',
          class: freight_class || '',
        },
        pallets: pallets?.length ? pallets : [{
          ...initialPallet
        }],
      }
    }));

  }, [shipments]);

  const isFirstRender = useRef(true);

  const havePalletsChanged = (shipmentDetails, shipmentsData) => {
    const serverShipments = shipmentDetails?.shipments || [];

    const serverPallets = serverShipments.map(s => ({
      amazonShipmentId: s.amazon_shipment_id,
      pallets: s.pallets?.map(p => ({
        height: p.height,
        width: p.width,
        length: p.length,
        weight: p.weight,
        isStacked: !!p.isStacked,
        noOfPallets: Number(p.noOfPallets),
      })) || []
    }));

    const localPallets = shipmentsData.map(s => ({
      amazonShipmentId: s.amazon_shipment_id,
      pallets: s.pallets?.map(p => ({
        height: p.height,
        width: p.width,
        length: p.length,
        weight: p.weight,
        isStacked: p.stackable === 'Yes',
        noOfPallets: Number(p.noOfPallets),
      })) || []
    }));

    return !isEqual(serverPallets, localPallets);
  };


  const handleAutoSavePalletData = useCallback(() => {
    const hasChanged =
      havePalletsChanged(shipmentDetails, shipmentsData) ||
      JSON.stringify(contactDetails) !== JSON.stringify(shipmentDetails?.contact_details);

    if (!hasChanged) return;

    const mappedPallets = shipmentsData.flatMap(shipment => {
      const { pallets, amazon_shipment_id: amazonShipmentId } = shipment;

      return pallets
        .map(({ id, noOfPallets, height, width, length, weight, stackable }) => ({
          id,
          noOfPallets: Number(noOfPallets),
          height,
          width,
          length,
          weight,
          isStacked: stackable === 'Yes',
          amazonShipmentId,
        }));
    });

    const shipmentsFreightInfo = shipmentsData.map(shipment => {
      const { freightInfo, amazon_shipment_id: amazonShipmentId } = shipment;


      const cleanedFreightInfo = {
        ...freightInfo,
        class: freightInfo.class === 'estimate' ? freightInfo.estimatedClass?.toFixed(2)?.toString() || freightInfo.tempoEstKey?.toFixed(2)?.toString() : freightInfo.class,
      };

      delete cleanedFreightInfo.estimatedClass;
      delete cleanedFreightInfo.tempoEstKey

      return {
        freightInfo: cleanedFreightInfo,
        amazonShipmentId,
      };
    });


    dispatch(CreatePallets({
      pallets: mappedPallets,
      deletedPallets,
      contactDetails,
      shipmentsFreightInfo,
      inboundPlanId
    })).then(({ payload }) => {
      if (payload?.success) {
        setDeletedPallets([]);
        // dispatch(GetWarehouseShipments({
        //   workOrderId,
        // }));
        // notification.success({
        //   message: 'Success',
        //   description: 'Information Saved Successfully',
        //   placement: 'topRight',
        // })
      }
    });

  }, [autoSave]);

  const debouncedAutoSave = useCallback(
    debounce(() => {
      if (!isFirstRender.current) {
        handleAutoSavePalletData();
      }
    }, 300),
    [autoSave]
  );

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    debouncedAutoSave();
    return () => debouncedAutoSave.cancel();
  }, [debouncedAutoSave]);


  const addPalletsRow = (shipmentIndex) => {
    setShipmentsData(shipmentsData => {
      const shipmentsDataClone = cloneDeep(shipmentsData);
      shipmentsDataClone[shipmentIndex] = {
        ...shipmentsDataClone[shipmentIndex],
        pallets: [{ ...initialPallet }, ...shipmentsDataClone[shipmentIndex].pallets]
      };
      return shipmentsDataClone;
    });
  };

  const removePalletsRow = ({ palletIndex, shipmentIndex }) => {
    setShipmentsData(shipmentsData => {
      const shipmentsDataClone = cloneDeep(shipmentsData);
      shipmentsDataClone[shipmentIndex] = {
        ...shipmentsDataClone[shipmentIndex],
        pallets: shipmentsDataClone[shipmentIndex].pallets.filter((_, index) => index !== palletIndex) || []
      };
      return shipmentsDataClone;
    });
    setAutoSave((prev) => !prev);
  };

  const handleFreightInfoUpdate = ({ shipmentIndex, key, value, estimatedExtraKey }) => {
    setShipmentsData(shipmentsData => {
      const shipmentsDataClone = cloneDeep(shipmentsData);
      const currentFreightInfo = shipmentsDataClone[shipmentIndex].freightInfo || {};
  
      shipmentsDataClone[shipmentIndex].freightInfo = {
        ...currentFreightInfo,
        [key]: value,
        ...(key === 'class' && estimatedExtraKey !== undefined && {
          tempoEstKey: estimatedExtraKey,
          estimatedClass: estimatedExtraKey
        }),
        // ✅ preserve estimatedClass if key !== 'class'
        ...(key !== 'class' && currentFreightInfo.class === 'estimate' && currentFreightInfo.estimatedClass && {
          estimatedClass: currentFreightInfo.estimatedClass
        }),
      };
  
      return shipmentsDataClone;
    });
    setAutoSave((prev) => !prev);
  };

  const calculateFreightClass = (pallets) => {
    if (!pallets?.length) return '';

    const LENGTH = 40;
    const WIDTH = 48;
    let highestFreightClass = 0;
    let totalVolumeCuFt = 0;
    let totalWeight = 0;

    const getFreightClass = (density) => {
      if (density > 50) return 50;
      if (density > 35) return 55;
      if (density > 30) return 60;
      if (density > 22.5) return 65;
      if (density > 15) return 70;
      if (density > 13.5) return 77.5;
      if (density > 12) return 85;
      if (density > 10.5) return 92.5;
      if (density > 9) return 100;
      if (density > 8) return 110;
      if (density > 7) return 125;
      if (density > 6) return 150;
      if (density > 5) return 175;
      if (density > 4) return 200;
      if (density > 3) return 250;
      if (density > 2) return 300;
      if (density > 1) return 400;
      return 500;
    };

    pallets.forEach(({ height, weight, stackable, noOfPallets }) => {
      if (height && weight) {
        const volume = (LENGTH * WIDTH * height) / 1728 * (Number(noOfPallets) || 1);
        const palletWeight = Number(weight) * (Number(noOfPallets) || 1);
        const density = palletWeight / volume;

        totalVolumeCuFt += volume;
        totalWeight += palletWeight;

        let freightClass = getFreightClass(density);

        if (stackable === 'No') {
          const currentIndex = sellerFreightClassValues.findIndex(option => option.value === Number(freightClass));
          freightClass = sellerFreightClassValues[Math.min(currentIndex + 1, sellerFreightClassValues.length - 1)]?.value;
        }

        if (Number(freightClass) > highestFreightClass) {
          highestFreightClass = Number(freightClass);
        }
      }
    });

    if (totalVolumeCuFt > 0) {
      const overallDensity = totalWeight / totalVolumeCuFt;
      const overallFreightClass = getFreightClass(overallDensity);

      highestFreightClass = Math.max(highestFreightClass, overallFreightClass);
    }

    return highestFreightClass;
  };

  const handlePalletUpdate = ({ shipmentIndex, palletIndex, key, value }) => {
    setShipmentsData(prevShipmentsData => {
      const updatedData = [...prevShipmentsData];
      const updatedShipment = cloneDeep(updatedData[shipmentIndex]);

      updatedShipment.pallets[palletIndex][key] = value;

      if (updatedShipment.freightInfo.class === 'estimate') {
        const newFreightClass = calculateFreightClass(updatedShipment.pallets);
        updatedShipment.freightInfo.estimatedClass = newFreightClass;
      }

      updatedData[shipmentIndex] = updatedShipment;
      return updatedData;
    });

    setAutoSave(prev => !prev);
  };

  const disabledDates = current => {
    return current < dayjs().startOf('day');
  };

  const { name, phone, email, shipDate } = contactDetails || {};

  return (
    <ContactInformationWrapper>
      <Spin tip="Loading" spinning={generatingTransportationOptions || generatingDeliveryWindowOptions || confirmingTransportationOption || deliveryWindowOptionsLoading || updateShipmentTrackingDetailsLoading}>
        <div className='items-info-wrapper'>
          <div className='info-inputs'>
            <p className='title-item'>Contact Information</p>
            <div className='info-items'>
              <Input
                label="Name"
                important
                placeholder="Enter"
                value={name}
                onChange={({ target }) => {
                  setContactDetails(contactDetails => ({ ...contactDetails, name: target.value }))
                  setAutoSave((prev) => !prev);
                }
                }
              />
              <Input
                label="Phone Number"
                important
                placeholder="Enter"
                value={phone}
                onChange={({ target }) => {
                  setContactDetails(contactDetails => ({ ...contactDetails, phone: target.value }));
                  setAutoSave((prev) => !prev);
                }}
              />
              <Input
                label="Email Address"
                important
                placeholder="Enter"
                value={email}
                onChange={({ target }) => {
                  setContactDetails(contactDetails => ({ ...contactDetails, email: target.value }));
                  setAutoSave((prev) => !prev);
                }}
              />
            </div>
          </div>
          <div className='info-inputs'>
            <p className='title-item'>Ship Date</p>
            <div className='date-time'>
              <span style={{ display: 'flex' }}>
                <label>Ready to Ship Date</label>
                <p style={{ color: 'red', fontSize: 12 }}>*</p>
              </span>
              <DatePicker
                disabledDate={disabledDates}
                className='new-date-picker-ready-to-ship'
                value={shipDate ? moment(shipDate) : null}
                format='YYYY-MM-DD'
                onChange={(momentObj, dateStr) => {
                  if(palletsChangeAllowed){
                  setContactDetails(contactDetails => ({ ...contactDetails, shipDate: dateStr }));
                  setAutoSave((prev) => !prev);
                  }
                  else{
                    setEditConfirmationModal(true);
                  }
                }}
              />
            </div>
          </div>
        </div>
        <div className={`cards-shipment-items-grid ${activeKey === '2' ? 'cards-shipment-items-grid--step2' : ''}`}>
          {shipmentsData.map(({
            amazon_reference_id,
            boxes_info,
            shipment_items_info,
            amazon_warehouse_id,
            amazon_shipment_confirmation_id,
            freightInfo,
            pallets
          }, shipmentIndex) => {
            const estimatedClass = calculateFreightClass(pallets);
            const customOptions = [
              {
                value: 'estimate',
                label: `Estimate (${estimatedClass})`,
                estimatedExtraKey: estimatedClass
              },
              ...sellerFreightClassValues.slice(1)
            ]
            return (
            <div className='grid-items' key={shipmentIndex}>
              <div className='grid-items-header-info'>
                <div className="shipment-header-titles">
                <h6>
                  {amazon_shipment_confirmation_id}
                  {activeKey === '3' && (
                    <>
                      <Divider type='vertical' />
                      {amazon_reference_id}
                    </>
                  )}
                </h6>
                  {activeKey === '3' && <p className='shipping-mode'>{shipmentType === 'SP' ? 'SPD' : 'LTL'}</p>}
                </div>
                <div className='grid-items-header-info-child-info'>
                  <p><span>Location:</span> <span>{amazon_warehouse_id}</span></p>
                  <Divider type="vertical" />
                  <p><span>SKUs:</span> <span>{shipment_items_info.length || '-'}</span></p>
                  <Divider type="vertical" />
                  <p><span>Units:</span> <span>{boxes_info.reduce((sum, curr) => sum + (curr.units || 0), 0)}</span></p>
                  <Divider type="vertical" />
                  <p><span>Boxes Count:</span> <span>{boxes_info.length || '-'}</span></p>
                </div>
              </div>
              <div className='items-ui-wrappers'>
                {shipmentType !== 'SP' && (
                  <div className='items'>
                    <p>Freight Information</p>
                    <div className='items-child'>
                      <Input
                        placeholder='Enter'
                        label='Total Value'
                        value={
                          isNaN(Number(freightInfo?.value)) || Number(freightInfo?.value) === 0
                            ? ''
                            : Number(freightInfo?.value)
                        }
                          onChange={(e) => {
                            if (palletsChangeAllowed) {
                              handleFreightInfoUpdate({ shipmentIndex, key: 'value', value: e.target.value })
                            }
                            else {
                              setEditConfirmationModal(true);
                            }
                          }}
                      />
                      <ReactSelect
                        menuPlacement='auto'
                        options={customOptions}
                        placeholder='Enter'
                        label='Freight Class'
                        value={
                          freightInfo?.class === 'estimate'
                            ? customOptions[0] 
                            : customOptions.find(option => option.value === (freightInfo?.class === 'estimate'
                              ? `Estimate (${freightInfo?.estimatedClass ?? '-'})`
                              : Number(freightInfo?.class)))
                        }
                        important
                        style={{ width: '100%' }}
                        onChange={(e) => {
                          if (palletsChangeAllowed){
                            handleFreightInfoUpdate({ shipmentIndex, key: 'class', value: e?.value, estimatedExtraKey: e?.estimatedExtraKey })
                          }
                          else{
                            setEditConfirmationModal(true);
                          }
                        }}
                      />
                    </div>
                  </div>
                )}
                {shipmentType !== 'SP' && (
                  <>
                    <div className='pallets-info'>
                      <span style={{ display: 'flex' }}>
                        <p>Pallets</p>
                        <p style={{ color: 'red', fontSize: 12 }}>*</p>
                      </span>
                      <Button
                        onClick={() => {
                          if (palletsChangeAllowed){
                          addPalletsRow(shipmentIndex);
                          setAutoSave((prev) => !prev);
                        }
                        else{
                            setEditConfirmationModal(true);
                        }
                        }}
                        text="Add New"
                      />
                    </div>
                    <div className='items-overlay-wrapper'>
                      {pallets.map(({
                        id = '',
                        height,
                        weight,
                        stackable,
                        noOfPallets,
                      }, palletIndex) => (
                        <div className='items-wrapper' key={`pallet-${palletIndex}`}>
                          <Input
                            placeholder='40'
                            label={palletIndex === 0 && 'Length (in)'}
                            disabled
                          />
                          <Input
                            placeholder='48'
                            label={palletIndex === 0 && 'Width (in)'}
                            disabled
                          />
                          <Input
                            placeholder='Enter'
                            important
                            label={palletIndex === 0 && 'Height (in)'}
                            value={height || ''}
                            onChange={({ target }) => { if (palletsChangeAllowed) { handlePalletUpdate({ palletIndex, shipmentIndex, key: 'height', value: target.value }) } else { setEditConfirmationModal(true)}} }
                          />
                          <Input
                            placeholder='Enter'
                            important
                            label={palletIndex === 0 && 'Weight (lb)'}
                            value={weight || ''}
                            onChange={({ target }) => { if (palletsChangeAllowed) { handlePalletUpdate({ palletIndex, shipmentIndex, key: 'weight', value: target.value }) } else { setEditConfirmationModal(true) } }}
                          />
                          <ReactSelect
                            menuPortalTarget
                            menuPlacement={pallets.length - 1 === palletIndex ? 'top' : 'auto' }
                            options={stackableOptions}
                            placeholder='Yes'
                            label={palletIndex === 0 && 'Stackable'}
                            important
                            style={{ width: '100%' }}
                            value={stackable ? stackableOptions.find(option => option.value === stackable) : null}
                            onChange={(e) => { if (palletsChangeAllowed) { handlePalletUpdate({ palletIndex, shipmentIndex, key: 'stackable', value: e?.value }) } else { setEditConfirmationModal(true)}} }
                          />
                          <Input
                            placeholder='Enter'
                            important
                            label={palletIndex === 0 && 'No. of Pallets'}
                            value={noOfPallets || ''}
                            onChange={({ target }) => { if (palletsChangeAllowed) { handlePalletUpdate({ palletIndex, shipmentIndex, key: 'noOfPallets', value: target?.value }) } else { setEditConfirmationModal(true) } }}
                          />
                          <div className='actions-wrapper'>
                            {palletIndex === 0 && <label>Actions</label>}
                            <img onClick={() => {
                              if (palletsChangeAllowed) {
                              if (id) {
                                setDeletedPallets(deletedPallets => [...deletedPallets, id]);
                              }
                                removePalletsRow({ palletIndex, shipmentIndex });
                            }
                            else{
                                setEditConfirmationModal(true)
                            }
                            }} src={DeleteIcon} alt='DeleteIcon' />
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
              {activeKey === '3' && !palletsChangeAllowed && (
                <StepThree
                  deliveryWindowOptionsLoading={deliveryWindowOptionsLoading}
                  updateShipmentTrackingDetailsLoading={updateShipmentTrackingDetailsLoading}
                  setTransportationDetails={setTransportationDetails}
                  transportationDetails={transportationDetails}
                  showTransportGlobal={showTransportGlobal}
                  shipmentIdOuter={shipmentsData[shipmentIndex]?.amazon_shipment_id}
                  setActiveKey={setActiveKey}
                />
              )}
            </div>
)})}
        </div>
      </Spin>

      <Modal
        saveText="Yes"
        className="Delete-modal-wrapper-item"
        onClose={() => {
          setEditConfirmationModal(false);
        }}
        onSave={() => {
          dispatch(RevertInboundPlanStatus({ inboundPlanId }))
            .then(() => {
              setPalletsChangeAllowed(true);
              setEditConfirmationModal(false);
              setActiveKey('2');
              setIsSubmitPackingInfo(true);
            });
        }}
        closeText="Cancel"
        centerAlign
        size="small"
        open={editConfirmationModal}
        removeIcon
      >
        <AlertModalStyleWrapper>
          <div className='alert-modal-wrapper'>
          <img src={AlterIcon1} alt='AlertIcon' title='AlertIcon' />
            <Typography fontSize={24} component="h3" marginTop={3}>Want to edit?</Typography>
          <p>Are you sure you want to edit the information? you will lose the Carrier information</p>
          </div>
        </AlertModalStyleWrapper>
      </Modal>
    </ContactInformationWrapper>
  )
}

export default ShipmentPallets;
